import { compileStyles } from 'lib/styled-helpers'
import { AttributedString } from 'lib/types'
import React, { FC, useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Icon } from 'ui/icon'

import { getProjectName } from '../../lib/get-project-name'

export type Props = {
  checked?: boolean
  label?: AttributedString | string | React.ReactElement
  disabled?: boolean
  onChange: (value: boolean) => void
  alignItems?: 'center' | 'flex-start' | 'flex-end'
  size?: 'big' | 'medium'
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void
  error?: string
  name?: string
  required?: boolean
  styleTank?: string
}

export const Checkbox: FC<Props> = ({
  label,
  onChange,
  onInput,
  disabled,
  checked = false,
  alignItems = 'center',
  size = 'medium',
  error,
  name,
  required,
  styleTank
}) => {
  const theme = useTheme()

  const [checkBoxValue, setCheckBoxValue] = useState(checked)

  useEffect(() => {
    setCheckBoxValue(checked)
  }, [checked])

  const handleCheckboxChange = (e: any) => {
    onChange(e?.target?.checked)
    setCheckBoxValue(() => e?.target?.checked)
  }

  return (
    <Label disabled={disabled} styleTank={styleTank}>
      <Input
        name={name}
        type="checkbox"
        checked={checkBoxValue}
        onChange={handleCheckboxChange}
        disabled={disabled}
        onInput={(e) => {
          onInput && onInput(e)
        }}
      />
      <LabelInner>
        <CheckboxContent alignItems={alignItems}>
          <Box checked={checkBoxValue} disabled={disabled} size={size}>
            {checkBoxValue &&
              (getProjectName() !== 'generic_v2' ? (
                <Arrow
                  xmlns="http://www.w3.org/2000/svg"
                  width={theme.spacing[5]}
                  height={theme.spacing[5]}
                  viewBox="0 0 24 24"
                  checked={checkBoxValue}
                >
                  <Path
                    fillRule="evenodd"
                    fill={'currentColor'}
                    d="M18.087 5L8.904 15.142l-2.99-3.326L4 13.888 8.93 19 20 7.073z"
                  />
                </Arrow>
              ) : (
                <Icon name="checkmark-generic-v2" color="#0F0F0F" />
              ))}
          </Box>
        </CheckboxContent>
        {typeof label === 'string' ? (
          <Name dangerouslySetInnerHTML={{ __html: required ? label + '*' : label }} disabled={disabled} />
        ) : (
          label
        )}
      </LabelInner>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Label>
  )
}

const Box = styled.div<{ checked?: boolean; disabled?: boolean; size: 'big' | 'medium' }>`
  ${({ size, theme }) =>
    size === 'medium'
      ? `
          width: calc(${theme.spacing[4]} + 0.2rem);
          height:  calc(${theme.spacing[4]} + 0.2rem);
          margin-right:  ${theme.spacing[3]};
          `
      : `
          width: calc(${theme.spacing[8]} + 0.2rem);
          height:  calc(${theme.spacing[8]} + 0.2rem);
          margin-right: ${theme.spacing[1]};
      `};

  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-shrink: 0;
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'initial')};
  user-select: none;
  background: ${({ theme, checked, disabled }) => {
    if (disabled) return '#828282'
    if (getProjectName() === 'generic_v2') return checked ? '#D9D9D9' : 'none'
    return checked ? theme.palette.brand.primary : 'transparent'
  }};
  border: 1px solid
    ${({ theme, checked, disabled }) => {
      if (disabled) return '#828282'
      if (getProjectName() === 'generic_v2') return checked ? '#D9D9D9' : '#828282'
      return checked ? theme.palette.brand.primary : theme.palette.grey[400]
    }};
  transition: 0.08s;
  border-radius: ${() => {
    if (getProjectName() === 'avito') return '0.4rem'
    return 0
  }};

  svg {
    font-size: ${() => {
      if (getProjectName() === 'generic_v2') return '12px !important'
      return null
    }};
  }
`

const Name = styled.div<{ disabled?: boolean }>`
  ${({ theme }) => `
    ${compileStyles(theme.textStyles.p2)};
    color: ${theme.palette.grey[900]};
  `};
  line-height: 14px;
  font-weight: ${() => getProjectName() === 'ora' && 400};

  margin-top: 2px;
  a {
    color: ${({ theme }) => (getProjectName() === 'ora' ? theme.palette.brand.secondary : theme.palette.brand.primary)};
    text-decoration: underline;
  }
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
`

const Label = styled.label<{ disabled?: boolean; styleTank?: string }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: auto;

  &:hover ${Box} {
    border-color: ${({ theme, disabled }) => {
      if (getProjectName() === 'generic_v2') return disabled ? null : '#D9D9D9'
      return disabled ? null : theme.palette.brand.primary
    }};
  }

  ${Name} {
    white-space: pre-wrap;
    color: ${({ theme, styleTank }) => {
      if (getProjectName() === 'generic_v2') return '#D9D9D9'
      return getProjectName() === 'tank' && styleTank && theme.palette.white
    }};
  }
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`

const LabelInner = styled.div`
  display: flex;
  align-items: ${() => {
    return getProjectName() === 'haval' ? 'center' : null
  }};
`

const Input = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: auto;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
`

const CheckboxContent = styled.div<{ alignItems: 'center' | 'flex-end' | 'flex-start' }>`
  display: flex;
  align-items: ${({ alignItems }) => alignItems};
`

// todo: Убрать стайлед-свг и path
const Arrow = styled.svg<{ checked: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  width: ${({ theme }) => theme.spacing[7]};
  height: ${({ theme }) => theme.spacing[5]};
  opacity: 0;
  transform: translate(-50%, -50%);

  ${({ checked }) =>
    checked &&
    `opacity: 1;

  `};
`

const Path = styled.path<{ disabled?: boolean }>`
  fill: ${({ disabled, theme }) => {
    if (getProjectName() === 'generic_v2') return disabled ? theme.palette.grey[500] : 'black'
    if (getProjectName() === 'tank') return disabled ? theme.palette.grey[500] : '#2C3232'
    return disabled ? theme.palette.grey[500] : theme.palette.white
  }}};
`

const ErrorMessage = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.palette.alert};
  padding: 4px 0;
  ${({ theme }) => theme.textStyles.p3};
  width: 100%;
  white-space: nowrap;
`
